import {
  HEX_IDENTIFIER_PATTERN,
  HEX_WITH_OPAQUE_SUFFIX_PATTERN,
  NUMERIC_ID_PATTERN,
  UUID_PATTERN,
  decodePathSegment,
  isMixedAlphanumericOpaqueToken,
} from './segment-identifier-patterns';
import { isKnownLocaleCatalogSegment } from './locale-segment-catalog';

export type SegmentValueClass =
  | 'identifier'
  | 'colon_delimited'
  | 'route_word'
  | 'other';

const DOLLAR_MARKED_TOKEN_PATTERN = /^\$[A-Za-z0-9_]+\$[_-][0-9a-fA-F-]+$/;
const DOLLAR_DELIMITED_OPAQUE_PATTERN = /^[a-z0-9]+\$[a-z0-9$-]+$/i;
const VERSION_SEGMENT_PATTERN = /^v\d+$/i;
const PREFIXED_ID_PATTERN = /^[a-z]+-\d+$/i;
const LETTER_PREFIX_NUMERIC_ID_PATTERN = /^[a-z]+\d{4,}$/i;
const BASE64URL_TOKEN_PATTERN = /^(?=.*[0-9])[A-Za-z0-9_-]{22,}$/;
const TEMPLATE_SEGMENT_PATTERN = /^\{[^}]+\}$/;
const ROUTE_WORD_PATTERN = /^[a-zA-Z][a-zA-Z0-9]*([_-][a-zA-Z0-9]+)*$/;
const SLUG_TOKEN_SEPARATOR_PATTERN = /[-_]/;
const SLUG_WORD_TOKEN_PATTERN = /^[A-Za-z]{3,}$/;
const SLUG_MIN_TOKEN_COUNT = 3;
const SLUG_MIN_WORD_TOKEN_RATIO = 0.5;
const SLUG_FILE_EXTENSION_PATTERN = /\.[a-zA-Z][a-zA-Z0-9]{1,4}$/;

export function splitSegmentIntoSlugTokens(segment: string): string[] {
  return segment.split(SLUG_TOKEN_SEPARATOR_PATTERN).filter(Boolean);
}

export function isSlugShapedSegment(segment: string): boolean {
  const withoutExtension = segment.replace(SLUG_FILE_EXTENSION_PATTERN, '');
  const tokens = splitSegmentIntoSlugTokens(withoutExtension);

  if (tokens.length < SLUG_MIN_TOKEN_COUNT) {
    return false;
  }

  const wordTokens = tokens.filter((token) =>
    SLUG_WORD_TOKEN_PATTERN.test(token),
  );

  return wordTokens.length / tokens.length >= SLUG_MIN_WORD_TOKEN_RATIO;
}

export function isLocaleShapedSegment(segment: string): boolean {
  if (!segment || segment.includes('/') || isSlugShapedSegment(segment)) {
    return false;
  }

  return isKnownLocaleCatalogSegment(segment);
}

function isOpaqueIdentifierSegment(decoded: string): boolean {
  return (
    UUID_PATTERN.test(decoded) ||
    HEX_IDENTIFIER_PATTERN.test(decoded) ||
    HEX_WITH_OPAQUE_SUFFIX_PATTERN.test(decoded) ||
    DOLLAR_MARKED_TOKEN_PATTERN.test(decoded) ||
    DOLLAR_DELIMITED_OPAQUE_PATTERN.test(decoded) ||
    isMixedAlphanumericOpaqueToken(decoded) ||
    (BASE64URL_TOKEN_PATTERN.test(decoded) && !isSlugShapedSegment(decoded))
  );
}

export function segmentValueClass(segment: string): SegmentValueClass {
  if (!segment) {
    return 'other';
  }

  const decoded = decodePathSegment(segment);

  if (VERSION_SEGMENT_PATTERN.test(decoded)) {
    return 'route_word';
  }

  if (TEMPLATE_SEGMENT_PATTERN.test(decoded)) {
    return 'identifier';
  }

  if (
    isOpaqueIdentifierSegment(decoded) ||
    NUMERIC_ID_PATTERN.test(decoded) ||
    PREFIXED_ID_PATTERN.test(decoded) ||
    LETTER_PREFIX_NUMERIC_ID_PATTERN.test(decoded)
  ) {
    return 'identifier';
  }

  if (decoded.includes(':')) {
    return 'colon_delimited';
  }

  if (ROUTE_WORD_PATTERN.test(decoded)) {
    return 'route_word';
  }

  if (/[a-zA-Z]/.test(decoded)) {
    return 'route_word';
  }

  return 'other';
}
